import React, {useEffect, useState} from 'react';
import {Alert, Box, Chip, Link as MuiLink, Paper, Stack, Typography} from '@mui/material';

export const isoDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const dayTitle = (date) => {
    const [y, m, d] = date.split('-').map(Number);
    const s = new Date(y, m - 1, d).toLocaleDateString('ru-RU', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
    return s.charAt(0).toUpperCase() + s.slice(1);
};

// Текущее время по Самаре (расписание указано по самарскому времени, а не по часам браузера).
export const samaraNow = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Samara', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date());
    const get = (t) => parts.find((p) => p.type === t).value;
    return {date: `${get('year')}-${get('month')}-${get('day')}`, minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute'))};
};

const useSamaraNow = () => {
    const [now, setNow] = useState(samaraNow);
    useEffect(() => {
        const timer = setInterval(() => setNow(samaraNow()), 30000);
        return () => clearInterval(timer);
    }, []);
    return now;
};

export const toMinutes = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
};

const fmtMinutes = (n) => {
    const h = Math.floor(n / 60);
    const m = n % 60;
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`;
};

// Конец пары; если его нет (в расписании группы бывает), считаем пару 90 минут.
export const lessonEnd = (l) => toMinutes(l.end) ?? (toMinutes(l.start) != null ? toMinutes(l.start) + 90 : null);

// Состояние пар сегодняшнего дня: какая идёт сейчас и какой промежуток между парами идёт (по времени Самары).
const todayState = (lessons, nowMin) => {
    let current = -1;
    let gapAfter = -1;
    lessons.forEach((l, i) => {
        const start = toMinutes(l.start);
        const end = lessonEnd(l);
        if (start == null || end == null) return;
        if (nowMin >= start && nowMin < end) current = i;
        const next = lessons[i + 1];
        const nextStart = next ? toMinutes(next.start) : null;
        if (nextStart != null && nowMin >= end && nowMin < nextStart) gapAfter = i;
    });
    return {current, gapAfter};
};

const roomLabel = (r) => (r.number ? `ауд. ${r.number}${r.building ? ` (корп. ${r.building})` : ''}` : r.code);

// Список дней расписания с парами. mode: 'teacher' — у пары группы, 'group' — преподаватели, 'room' — и преподаватели, и группы.
// onTeacher / onRoom (необязательные) делают имя преподавателя и кабинет ссылками.
const ScheduleDays = ({days, mode = 'teacher', onTeacher, onRoom}) => {
    const now = useSamaraNow();
    const todayIso = now.date;
    return (
        <Stack spacing={2}>
            {days.map((day) => (
                <Paper key={day.date} variant="outlined" sx={{borderRadius: 2, overflow: 'hidden', borderColor: day.date === todayIso ? 'primary.main' : undefined}}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{px: 2, py: 1, bgcolor: 'action.hover'}}>
                        <Typography fontWeight={700} sx={{flex: 1}}>{dayTitle(day.date)}</Typography>
                        {day.date === todayIso && <Chip size="small" color="primary" label="сегодня"/>}
                        {day.status && day.status !== 'confirmed' && <Chip size="small" color="warning" variant="outlined" label={day.status === 'predicted' ? 'прогноз' : day.status}/>}
                    </Stack>
                    {day.notes.map((n, i) => <Alert key={i} severity="info" sx={{borderRadius: 0}}>{n}</Alert>)}
                    {day.lessons.length === 0 ? (
                        <Typography variant="body2" color="text.disabled" sx={{px: 2, py: 1.5}}>Нет пар</Typography>
                    ) : (
                        <Stack divider={<Box sx={{borderTop: 1, borderColor: 'divider'}}/>}>
                            {day.lessons.map((l, i) => {
                                const isToday = day.date === todayIso;
                                const st = isToday ? todayState(day.lessons, now.minutes) : {current: -1, gapAfter: -1};
                                const isCurrent = st.current === i;
                                const end = lessonEnd(l);
                                const past = isToday && end != null && now.minutes >= end;
                                const nextStart = toMinutes(day.lessons[i + 1]?.start);
                                const gapHere = st.gapAfter === i && end != null && nextStart != null;
                                const gapLength = gapHere ? nextStart - end : 0;
                                return (
                                <React.Fragment key={i}>
                                <Stack direction="row" spacing={2} alignItems="flex-start"
                                       sx={{px: 2, py: 1.25, opacity: past ? 0.55 : 1,
                                            ...(isCurrent ? {bgcolor: 'rgba(67, 97, 238, 0.14)', boxShadow: (t) => `inset 4px 0 0 ${t.palette.primary.main}`} : {})}}>
                                    <Box sx={{minWidth: 92}}>
                                        <Typography fontWeight={700}>{l.start}{l.end ? `–${l.end}` : ''}</Typography>
                                        {l.pair != null && <Typography variant="caption" color="text.secondary">{l.pair}-я пара</Typography>}
                                    </Box>
                                    <Box sx={{flex: 1, minWidth: 0}}>
                                        <Typography>{l.subject}</Typography>
                                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{mt: 0.5}}>
                                            {(mode === 'teacher' || mode === 'room') && l.groups.map((g) => <Chip key={g} size="small" label={g}/>)}
                                            {(mode === 'group' || mode === 'room') && l.teachers.map((t) => (
                                                onTeacher
                                                    ? <MuiLink key={t} component="button" type="button" variant="body2" underline="hover" onClick={() => onTeacher(t)}>{t}</MuiLink>
                                                    : <Typography key={t} variant="body2" color="text.secondary">{t}</Typography>
                                            ))}
                                            {l.rooms.map((r) => (
                                                onRoom && r.code
                                                    ? <MuiLink key={r.code} component="button" type="button" variant="body2" underline="hover" color="text.secondary" onClick={() => onRoom(r.code)}>{roomLabel(r)}</MuiLink>
                                                    : <Typography key={r.code} variant="body2" color="text.secondary">{roomLabel(r)}</Typography>
                                            ))}
                                            {l.kind && l.kind !== 'lesson' && <Chip size="small" variant="outlined" label={l.kind}/>}
                                            {l.predicted && <Chip size="small" variant="outlined" color="warning" label="прогноз"/>}
                                            {l.lowConfidence && <Chip size="small" variant="outlined" color="warning" label="неточно"/>}
                                        </Stack>
                                        {l.note && <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>{l.note}</Typography>}
                                        {isCurrent && (
                                            <Chip size="small" color="primary" sx={{mt: 0.75}} label={`идёт сейчас · осталось ${fmtMinutes(end - now.minutes)}`}/>
                                        )}
                                    </Box>
                                </Stack>
                                {gapHere && (
                                    <Box sx={{px: 2, py: 0.75, bgcolor: 'rgba(255, 183, 77, 0.16)', boxShadow: (t) => `inset 4px 0 0 ${t.palette.warning.main}`}}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {gapLength <= 30 ? 'Перемена' : 'Окно между парами'} · следующая пара в {day.lessons[i + 1].start} (через {fmtMinutes(nextStart - now.minutes)})
                                        </Typography>
                                    </Box>
                                )}
                                </React.Fragment>
                                );
                            })}
                        </Stack>
                    )}
                </Paper>
            ))}
        </Stack>
    );
};

export default ScheduleDays;
